// Actions: payload of information, describing the fact that something happened
// There are three possible states for our login
// process and we need actions for each of them
import {
  API_BASE_URL,
  API_JWT_REFRESH_TOKEN_NAME,
  API_JWT_TOKEN_IAT,
  API_JWT_TOKEN_NAME,
  APP_USER_NAME,
  CALL_API,
} from './middleware/api'
import 'whatwg-fetch'
import SortData from './models/sort_data'
import { __isEnvDev, API_ENDPOINT_USER_TX, STORAGE_TYPE_LOCAL } from './config'
import StorageProviderFactory from './managers/storage_provider_factory'
import { buildUserTxDataUrl, buildUserTxDataUrlInsights, buildUserTxDataUrlDetails } from './utils/sort'
import { clearSession, isTokenExist, isTokenExpired } from './utils/session'
import { isResetPasswordPath } from './utils/routes'
import { rejectTransactionSort, sortRejectionMessage } from './actions/tx_grid_sort'

const storageProvider = StorageProviderFactory.create(STORAGE_TYPE_LOCAL)

export const LOGIN_REQUEST = 'LOGIN_REQUEST'
export const LOGIN_SUCCESS = 'LOGIN_SUCCESS'
export const LOGIN_FAILURE = 'LOGIN_FAILURE'

// todo split into separate action files

function requestLogin(creds) {
  return {
    type: LOGIN_REQUEST,
    creds,
  }
}

export function receiveLogin() {
  return {
    type: LOGIN_SUCCESS,
  }
}

export function loginError(message) {
  return {
    type: LOGIN_FAILURE,
    message,
  }
}

// Three possible states for our logout process as well.
// Since we are using JWTs, we just need to remove the token
// from storageProvider. These actions are more useful if we
// were calling the API to log the user out
export const LOGOUT_SUCCESS = 'LOGOUT_SUCCESS'

function receiveLogout() {
  return {
    type: LOGOUT_SUCCESS,
  }
}

// Logs the user out
export function logoutUser() {
  return (dispatch) => {
    clearSession()
    dispatch(receiveLogout())
    dispatch(setEphemeralToken(null))
    window.location.href = '/'
  }
}

export const TOKEN_EXPIRED = 'TOKEN_EXPIRED'

export function tokenExpired() {
  return {
    type: TOKEN_EXPIRED,
  }
}

export function checkSession() {
  return (dispatch) => {
    const tokenExists = isTokenExist()
    const expired = isTokenExpired()
    const token = storageProvider.getItem(API_JWT_TOKEN_NAME)
    if (!tokenExists || expired) {
      // if user session timed out, message will display on login page for 5 seconds
      if (token) {
        dispatch(loginError('Your session has timed out. Please sign in again.'))
        setTimeout(() => dispatch(clearLoginError), 5000)
      }
      clearSession()
      dispatch(tokenExpired())
      const { pathname } = window.location
      if (pathname !== '/' && !isResetPasswordPath(pathname)) {
        dispatch()
        // push({
        //   pathname: '/',
        //   state: { redirect_url: pathname }
        // })
      }
    }
  }
}

export function clearLoginError() {
  if (isTokenExpired()) {
    loginError('')
  }
}

// Calls the API to get a token and
// dispatches actions along the way
export function loginUser(creds) {
  let body = {}
  if (creds.code) {
    body = JSON.stringify({ code: creds.code, ephemeral_token: creds.ephemeral_token })
  } else {
    body = JSON.stringify({ username: creds.username, password: creds.password })
  }
  const config = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
  }
  const endpoint = creds.code ? '/auth/login/code/' : '/auth/login/'
  return (dispatch) => {
    // We dispatch requestLogin to kickoff the call to the API
    dispatch(requestLogin(creds))
    if (creds.username) {
      storageProvider.setItem(APP_USER_NAME, creds.username)
    }
    return window
      .fetch(API_BASE_URL + endpoint, config)
      .then((response) => response.json().then((data) => ({ data, response })))
      .then(({ data, response }) => {
        if (!response.ok) {
          // If there was a problem, we want to
          // dispatch the error condition
          dispatch(loginError(data.errors.non_field_errors[0]))
          return Promise.reject(data)
        }
        if (data.ephemeral_token) {
          dispatch(setEphemeralToken(data.ephemeral_token))
          return false
        }
        // If login was successful, set the token in local storage
        storageProvider.setItem(API_JWT_TOKEN_NAME, data.access)
        storageProvider.setItem(API_JWT_TOKEN_IAT, Math.round(Date.now() / 1000).toString())
        storageProvider.setItem(API_JWT_REFRESH_TOKEN_NAME, data.refresh)
        return true
      })
      .catch((err) => {
        if (__isEnvDev()) {
          console.log('Authentication request failed: ', err)
        }
        return false
      })
  }
}

export const USER_TX_REQUEST = 'USER_TX_REQUEST'
export const USER_TX_SUCCESS = 'USER_TX_SUCCESS'
export const USER_TX_FAILURE = 'USER_TX_FAILURE'

export const USER_TX_REQUEST_INSIGHTS = 'USER_TX_REQUEST_INSIGHTS'
export const USER_TX_SUCCESS_INSIGHTS = 'USER_TX_SUCCESS_INSIGHTS'
export const USER_TX_SUCCESS_INSIGHTS_FAST = 'USER_TX_SUCCESS_INSIGHTS_FAST'
export const USER_TX_FAILURE_INSIGHTS = 'USER_TX_FAILURE_INSIGHTS'

// Uses the API middleware to get all user transactions
/**
 * fetch paged transactions by state, search term
 * @param page
 * @param state
 * @param searchTerm
 * @param sortData
 * @param clientId
 * @param countryCode
 * @returns {{}}
 */

export function fetchUsersTx(page = 1, state, search, sortData, clientId, countryCode, pageSize = 20, dateRange) {
  let field
  let order
  if (sortData && sortData instanceof SortData) {
    field = sortData.getField()
    order = sortData.getOrder()
  }

  return {
    [CALL_API]: {
      endpoint: buildUserTxDataUrl(page, state, search, field, order, clientId, countryCode, pageSize, dateRange),
      types: [USER_TX_REQUEST, USER_TX_SUCCESS, USER_TX_FAILURE],
      method: 'GET',
      tokenType: API_JWT_TOKEN_NAME,
    },
  }
}

export const isOrderingRejection = (action) => Boolean(action && action.details && action.details.ordering)

export const fetchUsersTxOrUnsorted =
  (page, state, search, sortData, clientId, countryCode, pageSize, dateRange) => (dispatch) =>
    dispatch(fetchUsersTx(page, state, search, sortData, clientId, countryCode, pageSize, dateRange)).then((action) => {
      if (!isOrderingRejection(action)) {
        return action
      }
      dispatch(rejectTransactionSort(sortRejectionMessage(sortData && sortData.getField())))
      return dispatch(
        fetchUsersTx(page, state, search, new SortData(null, null), clientId, countryCode, pageSize, dateRange)
      )
    })

export function fetchUsersTxInsights(
  page = null,
  state = null,
  search = null,
  sortBy = null,
  order = null,
  client_id = null,
  transaction_identity__country_code = null,
  transaction_identity = null,
  page_size = null,
  created_by = null,
  error = null,
  face_match_min = null,
  face_match_max = null,
  image = null,
  fast = false
) {
  let field
  if (sortBy && sortBy instanceof SortData) {
    field = sortBy.getField()
    order = sortBy.getOrder()
  }

  return {
    [CALL_API]: {
      endpoint: buildUserTxDataUrlInsights(
        page,
        state,
        search,
        field,
        order,
        client_id,
        transaction_identity__country_code,
        transaction_identity,
        page_size,
        created_by,
        error,
        face_match_min,
        face_match_max,
        image,
        fast
      ),
      types: [
        USER_TX_REQUEST_INSIGHTS,
        fast ? USER_TX_SUCCESS_INSIGHTS_FAST : USER_TX_SUCCESS_INSIGHTS,
        USER_TX_FAILURE_INSIGHTS,
      ],
      method: 'GET',
      tokenType: API_JWT_TOKEN_NAME,
    },
  }
}

export const USER_TX_FAST_REQUEST = 'USER_TX_FAST_REQUEST'
export const USER_TX_FAST_SUCCESS = 'USER_TX_FAST_SUCCESS'
export const USER_TX_FAST_FAILURE = 'USER_TX_FAST_FAILURE'

export function fetchUsersTxFast(
  page = null,
  state = null,
  searchTerm = null,
  sortBy = null,
  order = null,
  countryCode = null,
  index = 1,
  dateRange,
  pageSize = 10
) {
  let field
  if (sortBy && sortBy instanceof SortData) {
    field = sortBy.getField()
    order = sortBy.getOrder()
  }
  return {
    [CALL_API]: {
      endpoint: buildUserTxDataUrlDetails(
        page,
        state,
        searchTerm,
        field,
        order,
        countryCode,
        index,
        dateRange,
        pageSize
      ),
      types: [USER_TX_FAST_REQUEST, USER_TX_FAST_SUCCESS, USER_TX_FAST_FAILURE],
      method: 'GET',
      tokenType: API_JWT_TOKEN_NAME,
    },
  }
}

export const USER_TX_ONE_PAGE_REQUEST = 'USER_TX_ONE_PAGE_REQUEST'
export const USER_TX_ONE_PAGE_SUCCESS = 'USER_TX_ONE_PAGE_SUCCESS'
export const USER_TX_ONE_PAGE_FAILURE = 'USER_TX_ONE_PAGE_FAILURE'

export function getSpecificPage(url, direction) {
  return {
    [CALL_API]: {
      endpoint: `/api/transactions/${url.split('/api/transactions/')[1]}`,
      types: [USER_TX_ONE_PAGE_REQUEST, USER_TX_ONE_PAGE_SUCCESS, USER_TX_ONE_PAGE_FAILURE],
      method: 'GET',
      tokenType: API_JWT_TOKEN_NAME,
    },
  }
}

// API call to get a single transaction by id
export const USER_TX_ID_REQUEST = 'USER_TX_ID_REQUEST'
export const USER_TX_ID_SUCCESS = 'USER_TX_ID_SUCCESS'
export const USER_TX_ID_FAILURE = 'USER_TX_ID_FAILURE'
export const USER_TX_ID_RESET = 'USER_TX_ID_RESET'

export function fetchUserTxById(id) {
  return {
    [CALL_API]: {
      endpoint: `${API_ENDPOINT_USER_TX}/${id}/`,
      types: [USER_TX_ID_REQUEST, USER_TX_ID_SUCCESS, USER_TX_ID_FAILURE],
      method: 'GET',
      tokenType: API_JWT_TOKEN_NAME,
    },
  }
}

export function resetFetchUserTxById() {
  return {
    type: USER_TX_ID_RESET,
  }
}

// API call to update transaction state
export const USER_TX_STATE_UPDATE_REQUEST = 'USER_TX_STATE_UPDATE_REQUEST'
export const USER_TX_STATE_UPDATE_SUCCESS = 'USER_TX_STATE_UPDATE_SUCCESS'
export const USER_TX_STATE_UPDATE_FAILURE = 'USER_TX_STATE_UPDATE_FAILURE'

export function updateTxState(id, stateName, body) {
  return {
    [CALL_API]: {
      endpoint: `${API_ENDPOINT_USER_TX}/${id}/make-${stateName}/`,
      types: [USER_TX_STATE_UPDATE_REQUEST, USER_TX_STATE_UPDATE_SUCCESS, USER_TX_STATE_UPDATE_FAILURE],
      method: 'PUT',
      body,
      tokenType: API_JWT_TOKEN_NAME,
    },
  }
}

// API call to support 2FA
export const CHECK_MFA_REQUEST = 'CHECK_MFA_REQUEST'
export const CHECK_MFA_SUCCESS = 'CHECK_MFA_SUCCESS'
export const CHECK_MFA_FAILURE = 'CHECK_MFA_FAILURE'

export const ACTIVATE_MFA_REQUEST = 'ACTIVATE_MFA_REQUEST'
export const ACTIVATE_MFA_SUCCESS = 'ACTIVATE_MFA_SUCCESS'
export const ACTIVATE_MFA_FAILURE = 'ACTIVATE_MFA_FAILURE'

export const DISABLE_MFA_REQUEST = 'DISABLE_MFA_REQUEST'
export const DISABLE_MFA_SUCCESS = 'DISABLE_MFA_SUCCESS'
export const DISABLE_MFA_FAILURE = 'DISABLE_MFA_FAILURE'

export const CONFIRM_MFA_REQUEST = 'CONFIRM_MFA_REQUEST'
export const CONFIRM_MFA_SUCCESS = 'CONFIRM_MFA_SUCCESS'
export const CONFIRM_MFA_FAILURE = 'CONFIRM_MFA_FAILURE'

export const SET_EPHEMERAL_TOKEN = 'SET_EPHEMERAL_TOKEN'

export function setEphemeralToken(token) {
  return {
    type: SET_EPHEMERAL_TOKEN,
    token,
  }
}

export function checkMfaStatus() {
  return {
    [CALL_API]: {
      endpoint: '/auth/mfa/user-active-methods/',
      types: [CHECK_MFA_REQUEST, CHECK_MFA_SUCCESS, CHECK_MFA_FAILURE],
      method: 'GET',
      tokenType: API_JWT_TOKEN_NAME,
    },
  }
}

export function activateMfa() {
  return {
    [CALL_API]: {
      endpoint: '/auth/app/activate/',
      types: [ACTIVATE_MFA_REQUEST, ACTIVATE_MFA_SUCCESS, ACTIVATE_MFA_FAILURE],
      method: 'POST',
      tokenType: API_JWT_TOKEN_NAME,
    },
  }
}

export function disableMfa() {
  return {
    [CALL_API]: {
      endpoint: '/auth/app/deactivate/',
      types: [DISABLE_MFA_REQUEST, DISABLE_MFA_SUCCESS, DISABLE_MFA_FAILURE],
      method: 'POST',
      tokenType: API_JWT_TOKEN_NAME,
    },
  }
}

export function confirmMfa(code) {
  return {
    [CALL_API]: {
      endpoint: '/auth/app/activate/confirm/',
      types: [CONFIRM_MFA_REQUEST, CONFIRM_MFA_SUCCESS, CONFIRM_MFA_FAILURE],
      method: 'POST',
      tokenType: API_JWT_TOKEN_NAME,
      body: { code },
    },
  }
}
